import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Tag, Plus, Trash2, Save, Loader2, X, Pencil } from "lucide-react";

const empty = { code: "", type: "percent", value: 10, minAmount: 0, maxUses: 0, validFrom: "", validUntil: "", active: true };

const ShopPromoAdmin = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/shop/promo-codes"); setCodes(data || []); }
    catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const c = editing;
    if (!c.code.trim()) { toast.error("Le code est obligatoire"); return; }
    if (Number(c.value) <= 0) { toast.error("La valeur de la remise doit être supérieure à 0"); return; }
    setSaving(true);
    const body = { ...c, code: c.code.trim().toUpperCase(), value: Number(c.value), minAmount: Number(c.minAmount) || 0, maxUses: Number(c.maxUses) || 0, validFrom: c.validFrom || null, validUntil: c.validUntil || null };
    try {
      if (c.id) await api.put(`/admin/shop/promo-codes/${c.id}`, body);
      else await api.post("/admin/shop/promo-codes", body);
      toast.success("Code enregistré");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer le code ${c.code} ?`)) return;
    try { await api.delete(`/admin/shop/promo-codes/${c.id}`); toast.success("Code supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  return (
    <div data-testid="shop-promo-admin">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800 flex items-center gap-2"><Tag size={24} /> Codes de réduction</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Pourcentage ou montant fixe, période de validité, nombre d&apos;utilisations et montant minimum d&apos;achat.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-full text-sm shrink-0" data-testid="shop-promo-new">
          <Plus size={15} /> Nouveau code
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-400"><Loader2 className="animate-spin inline" size={20} /></div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm p-4">
          {codes.length === 0 ? (
            <p className="text-stone-400 italic text-sm py-4 text-center">Aucun code de réduction.</p>
          ) : (
            <div className="space-y-2" data-testid="shop-promo-list">
              {codes.map((c) => (
                <div key={c.id} className="flex items-center gap-3 border border-stone-100 rounded-xl p-3 flex-wrap" data-testid={`shop-promo-${c.code}`}>
                  <span className="font-mono text-sm bg-blush-light text-deep-rose px-2.5 py-1 rounded-lg">{c.code}</span>
                  <span className="text-sm text-stone-700">{c.type === "percent" ? `-${c.value} %` : `-${Number(c.value).toFixed(2)} €`}</span>
                  <span className="text-xs text-stone-500">
                    {c.minAmount > 0 ? `dès ${Number(c.minAmount).toFixed(2)} € · ` : ""}
                    {c.maxUses > 0 ? `${c.usedCount || 0}/${c.maxUses} utilisé(s)` : `${c.usedCount || 0} utilisation(s)`}
                    {c.validUntil ? ` · jusqu'au ${c.validUntil}` : ""}
                  </span>
                  {!c.active && <span className="text-[10px] uppercase bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">Désactivé</span>}
                  <div className="ml-auto flex gap-1.5">
                    <button onClick={() => setEditing({ ...empty, ...c, validFrom: (c.validFrom || "").slice(0, 10), validUntil: (c.validUntil || "").slice(0, 10) })} className="p-2 rounded-lg hover:bg-stone-100 text-stone-600" data-testid={`shop-promo-edit-${c.code}`}><Pencil size={14} /></button>
                    <button onClick={() => remove(c)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-600" data-testid={`shop-promo-delete-${c.code}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-start justify-center p-3 overflow-y-auto" onClick={() => setEditing(null)} data-testid="shop-promo-modal">
          <div className="bg-white rounded-2xl w-full max-w-md my-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <h3 className="font-serif-display text-stone-800">{editing.id ? "Modifier le code" : "Nouveau code"}</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-stone-100" data-testid="shop-promo-modal-close"><X size={17} /></button>
            </div>
            <div className="p-4 space-y-3.5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Code *</label>
                <input value={editing.code} onChange={(e) => setField("code", e.target.value.toUpperCase())} placeholder="NOEL2026" data-testid="shop-promo-code" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 font-mono uppercase" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Type</label>
                  <select value={editing.type} onChange={(e) => setField("type", e.target.value)} data-testid="shop-promo-type" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm">
                    <option value="percent">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (€)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Valeur *</label>
                  <input type="number" step="0.5" value={editing.value} onChange={(e) => setField("value", e.target.value)} data-testid="shop-promo-value" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Achat minimum (€)</label>
                  <input type="number" step="1" value={editing.minAmount} onChange={(e) => setField("minAmount", e.target.value)} data-testid="shop-promo-min" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Utilisations max</label>
                  <input type="number" step="1" value={editing.maxUses} onChange={(e) => setField("maxUses", e.target.value)} placeholder="0 = illimité" data-testid="shop-promo-maxuses" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Valable du</label>
                  <input type="date" value={editing.validFrom || ""} onChange={(e) => setField("validFrom", e.target.value)} data-testid="shop-promo-from" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Jusqu&apos;au</label>
                  <input type="date" value={editing.validUntil || ""} onChange={(e) => setField("validUntil", e.target.value)} data-testid="shop-promo-until" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setField("active", e.target.checked)} data-testid="shop-promo-active" className="rounded" /> Code actif
              </label>
              <button onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-coral text-white px-5 py-3 rounded-full text-sm disabled:opacity-50" data-testid="shop-promo-save">
                {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopPromoAdmin;
